/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import tw from 'tailwind-styled-components'

import ErrorLabel from '@/ui/atoms/ErrorLabel'
import Flex from '@/ui/atoms/Flex'
import FlexBox from '@/ui/atoms/FlexBox'
import Icon, { IconSources } from '@/ui/atoms/Icon'
import Text from '@/ui/atoms/Text'
import cn from '@/utils/tailwind/cn'

import Tooltip from '../atoms/Tooltip'

export type Props = {
  children?: React.ReactNode
  compact?: boolean
  errorLabel?: string | null | undefined
  hasError?: boolean
  horizontal?: boolean
  isOfficeUse?: boolean
  isRequired?: boolean
  label?: void | string | React.ReactNode
  mutedLabel?: boolean
  reverse?: boolean
  semibold?: boolean
  smallLabel?: boolean
  suffix?: React.ReactNode
  noRelative?: boolean
}

const Suffix = tw.span`
  absolute
  inset-y-0
  right-0
  flex
  items-center
  pr-2
`

function BaseField({
  children,
  compact,
  errorLabel,
  hasError,
  horizontal,
  isOfficeUse,
  isRequired,
  label,
  mutedLabel,
  reverse,
  semibold,
  smallLabel,
  suffix,
  noRelative,
}: Props) {
  const history = useHistory()
  const isPreview = history.location.pathname.includes('/r/preview')
  const isPatientForm = history.location.pathname.includes('/f/')
  const isKioskMode = history.location.pathname.includes('/k/')
  const isForm = history.location.pathname.includes('/r/')
  const isPacket = history.location.pathname.includes('/t/p/')

  const hasQuestionText =
    isPreview || isPatientForm || isForm || isPacket || isKioskMode

  const renderLabel = React.useCallback(() => {
    if (!label) return null

    const text = (
      <Flex $horizontalSpacing="xsmall" className="items-center">
        {isOfficeUse ? (
          <Tooltip mouseEnterDelay={0.7} title="Office use only">
            <i>
              <Icon name={IconSources.OFFICE_USE} size={20} />
            </i>
          </Tooltip>
        ) : null}
        <Text
          $muted={mutedLabel}
          $semibold={semibold}
          $small={smallLabel}
          className={cn(hasQuestionText && 'question-text')}
        >
          {label}
        </Text>
        {isRequired ? <ErrorLabel>*</ErrorLabel> : null}
      </Flex>
    )

    if (compact) return text

    return <FlexBox>{text}</FlexBox>
  }, [
    label,
    isOfficeUse,
    mutedLabel,
    semibold,
    smallLabel,
    hasQuestionText,
    isRequired,
    compact,
  ])

  const renderError = React.useCallback(() => {
    if (!hasError) return

    return <ErrorLabel>{errorLabel}</ErrorLabel>
  }, [errorLabel, hasError])

  const renderSuffix = React.useCallback(() => {
    if (!suffix) return null

    // @ts-ignore
    return <Suffix>{suffix}</Suffix>
  }, [suffix])

  const renderChildren = React.useCallback(() => {
    return (
      <div className={cn(!noRelative && 'relative')}>
        {children}
        {renderSuffix()}
      </div>
    )
  }, [children, renderSuffix, noRelative])

  const renderContent = React.useCallback(() => {
    if (reverse) {
      return (
        <>
          <div className="flex-1">{renderChildren()}</div>
          {renderLabel()}
          {renderError()}
        </>
      )
    }

    return (
      <>
        {label ? <div className="mb-1 flex-1">{renderLabel()}</div> : null}
        {renderChildren()}
        {renderError()}
      </>
    )
  }, [renderChildren, renderError, renderLabel, reverse, label])

  return (
    <Flex
      $flexDirection={horizontal ? 'row' : 'col'}
      $horizontalSpacing={compact ? 'small' : null}
    >
      {renderContent()}
    </Flex>
  )
}

export default BaseField
